import React, { useEffect, useState } from 'react';
import { IAnalyticsFilters, IAnalyticsOrdersResponse } from '@/interfaces/Dashboard/dashboard.interface';
import { AnalyticsExportButton } from './AnalyticsExportButton';
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { ShoppingBag, CheckCircle2, Clock, ThumbsUp, Package, Truck, XCircle, AlertCircle, Percent, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { CommonCardSkeleton, LineChartSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';
import { NoDataFound } from '@/components/common/NoDataFound';
import { formatRand, formatChartDate } from '@/utility/helper';

interface OrdersSectionProps {
    filters: IAnalyticsFilters;
    data: IAnalyticsOrdersResponse['data'] | null;
    isLoading: boolean;
}


export const OrdersSection: React.FC<OrdersSectionProps> = ({ filters, data, isLoading }) => {

    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="flex justify-between items-center mb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Array.from({ length: 9 }).map((_, i) => <CommonCardSkeleton key={i} />)}
                </div>
            </div>
        );
    }

    const kpis = data?.kpis || {} as any;
    const metrics = data?.metrics || {} as any;
    const charts = data?.charts || {} as any;

    const kpiCards = [
        { label: 'Total Orders', value: kpis.totalOrders ?? 0, icon: <ShoppingBag />, color: '#3B82F6' },
        { label: 'Completed Orders', value: kpis.completed ?? 0, icon: <CheckCircle2 />, color: '#10B981' },
        { label: 'Pending Orders', value: kpis.pending ?? 0, icon: <Clock />, color: '#F59E0B' },
        { label: 'Accepted Orders', value: kpis.accepted ?? 0, icon: <ThumbsUp />, color: '#3B82F6' },
        { label: 'Preparing Orders', value: kpis.inProgress ?? 0, icon: <Package />, color: '#8B5CF6' },
        { label: 'Picked-Up Orders', value: kpis.pickedUp ?? 0, icon: <Truck />, color: '#EC4899' },
        { label: 'Out for Delivery', value: kpis.outForDelivery ?? 0, icon: <Truck />, color: '#14B8A6' },
        { label: 'Cancelled Orders', value: kpis.cancelled ?? 0, icon: <XCircle />, color: '#EF4444' },
        { label: 'Failed Orders', value: kpis.failed ?? 0, icon: <AlertCircle />, color: '#000000' },
    ];

    const metricCards = [
        { label: 'Completion Rate', value: `${metrics.completionRate ?? 0}%`, icon: <Percent />, color: '#10B981' },
        { label: 'Cancellation Rate', value: `${metrics.cancellationRate ?? 0}%`, icon: <Percent />, color: '#EF4444' },
        { label: 'Average Order Value', value: formatRand(metrics.avgOrderValue), icon: <ShoppingBag />, color: '#3B82F6' },
        { label: 'Avg Items per Order', value: metrics.avgItemsPerOrder ?? 0, icon: <Package />, color: '#8B5CF6' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Orders & Status</h2>
                <AnalyticsExportButton
                    exportUrl="/reports/analytics/export?type=orders"
                    fileName="orders_report.xlsx"
                    filters={filters}
                />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
                {kpiCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {metricCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Order Trend" icon={<TrendingUp />}>
                    <div className="h-[300px] w-full">
                        {!charts.orderTrend || charts.orderTrend.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={charts.orderTrend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Area type="monotone" dataKey="orders" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} strokeWidth={3} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="Order Status Distribution" icon={<PieChart />}>
                    <div className="h-[300px] w-full flex items-center justify-center">
                        {!charts.statusDistribution || charts.statusDistribution.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={charts.statusDistribution}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="count"
                                        nameKey="status"
                                    >
                                        {charts.statusDistribution.map((entry, index) => (
                                            <Cell key={`cell-\${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Orders by Hour" icon={<Clock />}>
                    <div className="h-[300px] w-full">
                        {!charts.ordersByHour || charts.ordersByHour.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={charts.ordersByHour}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        cursor={{ fill: '#F3F4F6' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} barSize={24} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="Orders by Weekday" icon={<Clock />}>
                    <div className="h-[300px] w-full">
                        {!charts.ordersByWeekday || charts.ordersByWeekday.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={charts.ordersByWeekday}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        cursor={{ fill: '#F3F4F6' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar dataKey="count" fill="#14B8A6" radius={[4, 4, 0, 0]} barSize={24} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>
        </div>
    );
};
